import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUser, Show } from "@clerk/react";
import { AuthModal } from "@/components/ui/auth-modal";
import { CreditsModal } from "@/components/ui/credits-modal";
import { useGenerate } from "@/hooks/use-generation";
import { useUserStore } from "@/stores/user-store";
import { trackImageUploaded, trackCreditsModalShown } from "@/services/analytics";
import {
  getGenerationCreditCost,
  getModelOption,
  INTEGRATED_MODEL_ID,
  AFFILIATE_DEFAULT_MODEL_ID,
  INTEGRATED_DURATION_MIN,
  type ModelId,
} from "@/config/models";
import { UploadZone } from "./upload-zone";
import { ModelSelector } from "./model-selector";
import { NsfwOrError } from "@/components/ui/nsfw-error";
import { Loader2, Sparkles, Star } from "lucide-react";

const DRAFT_KEY = "couchstack_draft_i2v";

function saveDraft(imageUrl: string | null, prompt: string, modelId: string) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ imageUrl, prompt, modelId }));
  } catch {}
}

function loadDraft(): { imageUrl: string | null; prompt: string; modelId?: string } | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && (parsed.imageUrl || parsed.prompt)) return parsed;
  } catch {}
  return null;
}

function clearDraft() {
  try { localStorage.removeItem(DRAFT_KEY); } catch {}
}

const AUTOGEN_KEY = "couchstack_autogen_pending";
function setPendingAutogen() { try { localStorage.setItem(AUTOGEN_KEY, String(Date.now())); } catch {} }
function hasPendingAutogen() {
  try {
    const v = localStorage.getItem(AUTOGEN_KEY);
    const t = v ? Number(v) : 0;
    return t > 0 && Date.now() - t < 15 * 60 * 1000;
  } catch { return false; }
}
function clearPendingAutogen() { try { localStorage.removeItem(AUTOGEN_KEY); } catch {} }

function getDeviceType(): "mobile" | "tablet" | "desktop" {
  const width = window.innerWidth;
  if (width < 768) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

type ImageMeta = { width: number; height: number; megapixels: number };

// Images carried over from the landing page or restored from a draft never pass
// through the upload handler, so their dimensions are unknown at generate time —
// which is the majority of first generations. Measure on demand instead. Capped
// so analytics can never hold up a generation; the bitmap is normally already
// decoded because it is on screen.
function measureImage(url: string, timeoutMs = 500): Promise<ImageMeta | null> {
  return new Promise((resolve) => {
    let settled = false;
    const done = (meta: ImageMeta | null) => {
      if (settled) return;
      settled = true;
      resolve(meta);
    };
    const timer = setTimeout(() => done(null), timeoutMs);
    const img = new Image();
    img.onload = () => {
      clearTimeout(timer);
      const { naturalWidth: w, naturalHeight: h } = img;
      done(w && h ? { width: w, height: h, megapixels: (w * h) / 1e6 } : null);
    };
    img.onerror = () => {
      clearTimeout(timer);
      done(null);
    };
    img.src = url;
  });
}

interface GenerateFormProps {
  initialImageUrl?: string | null;
  initialPrompt?: string;
  forceAuth?: boolean;
  affiliateFunnel?: boolean;
}

export function GenerateForm({ initialImageUrl, initialPrompt, forceAuth, affiliateFunnel }: GenerateFormProps = {}) {
  const navigate = useNavigate();
  const { isSignedIn, user: clerkUser } = useUser();
  const { credits, hasPurchased, isLoading: isUserLoading } = useUserStore();
  const generateMutation = useGenerate();
  const freeCreditsLocked = !hasPurchased;

  const hasProps = !!(initialImageUrl || initialPrompt);
  const draft = hasProps ? null : loadDraft();

  const [imageUrl, setImageUrl] = useState<string | null>(initialImageUrl ?? draft?.imageUrl ?? null);
  const [imageMeta, setImageMeta] = useState<ImageMeta | null>(null);
  // Whether the image was picked here or arrived with the user. Distinguishes the
  // landing-page funnel from in-app use, which the timing fields cannot do.
  const [imageSource, setImageSource] = useState<"upload" | "carried" | null>(
    initialImageUrl ?? draft?.imageUrl ? "carried" : null,
  );
  const [prompt, setPrompt] = useState(initialPrompt || draft?.prompt || "");
  const [promptEditCount, setPromptEditCount] = useState(0);
  const formMountedAt = useRef(Date.now());
  const [modelId, setModelId] = useState<ModelId>(() => {
    if (affiliateFunnel) return AFFILIATE_DEFAULT_MODEL_ID;
    const fromDraft = draft?.modelId as ModelId | undefined;
    if (fromDraft === "wan25_720p_i2v_5s") return AFFILIATE_DEFAULT_MODEL_ID;
    return fromDraft ?? AFFILIATE_DEFAULT_MODEL_ID;
  });
  const [integratedSeconds, setIntegratedSeconds] = useState(INTEGRATED_DURATION_MIN);
  const purchasedDefaultApplied = useRef(false);

  useEffect(() => {
    if (imageUrl || prompt) saveDraft(imageUrl, prompt, modelId);
  }, [imageUrl, prompt, modelId]);

  useEffect(() => {
    if (isUserLoading) return;
    if (freeCreditsLocked) {
      setModelId(AFFILIATE_DEFAULT_MODEL_ID);
      purchasedDefaultApplied.current = false;
      return;
    }
    if (purchasedDefaultApplied.current) return;
    purchasedDefaultApplied.current = true;
    setModelId(INTEGRATED_MODEL_ID);
  }, [freeCreditsLocked, isUserLoading]);

  const [showAuth, setShowAuth] = useState(false);
  const [authTitle, setAuthTitle] = useState("Sign Up to View Generation");
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [fakeLoading, setFakeLoading] = useState(false);

  const openAuthModal = useCallback((title = "Sign Up to View Generation") => {
    setPendingAutogen();
    setAuthTitle(title);
    setShowAuth(true);
  }, []);

  useEffect(() => {
    if (!forceAuth || isSignedIn) return;
    setPendingAutogen();
    setAuthTitle("Sign Up to View Generation");
    setFakeLoading(true);
    const timer = setTimeout(() => {
      setFakeLoading(false);
      setShowAuth(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, [forceAuth, isSignedIn]);

  const effectiveModelId = freeCreditsLocked ? AFFILIATE_DEFAULT_MODEL_ID : modelId;
  const selectedModel = getModelOption(effectiveModelId)!;
  const canGenerate = !!imageUrl && prompt.trim().length > 0;
  const displayCost = getGenerationCreditCost(
    selectedModel,
    effectiveModelId === INTEGRATED_MODEL_ID ? integratedSeconds : undefined,
  );

  const handleImageChange = useCallback((url: string | null, meta?: { width: number; height: number }) => {
    setImageUrl(url);
    if (url && meta) {
      const megapixels = (meta.width * meta.height) / 1e6;
      setImageMeta({ width: meta.width, height: meta.height, megapixels });
      setImageSource("upload");
      trackImageUploaded({
        user_id: clerkUser?.id,
        image_width: meta.width,
        image_height: meta.height,
        image_megapixels: megapixels,
        device_type: getDeviceType(),
      });
    } else if (url) {
      // Measuring can fail, and keeping the previous image's dimensions here would
      // silently mis-describe the new one. Clear and let the generate-time
      // fallback re-measure from the URL.
      setImageMeta(null);
      setImageSource("upload");
    } else {
      setImageMeta(null);
      setImageSource(null);
    }
  }, [clerkUser?.id]);

  const handleGenerate = useCallback(async ({ autogen = false }: { autogen?: boolean } = {}) => {
    if (!imageUrl || !prompt.trim()) return;
    if (credits < displayCost) {
      trackCreditsModalShown({
        user_id: clerkUser?.id,
        source: "generate_insufficient_credits",
        has_purchased: hasPurchased,
      });
      setShowCreditsModal(true);
      return;
    }
    // An autogen run fires from an effect the moment the user lands signed-in, so
    // ms_on_form and prompt_edit_count are near-zero by construction on that path.
    // Send the flag so those fields can be read conditionally rather than being
    // mistaken for a deliberate, fast user.
    const meta = imageMeta ?? (imageUrl ? await measureImage(imageUrl) : null);
    if (meta && !imageMeta) setImageMeta(meta);
    try {
      const params: Record<string, unknown> = {
        model_id: effectiveModelId,
        prompt: prompt.trim(),
        reference_image: imageUrl,
        device_type: getDeviceType(),
        ms_on_form: Date.now() - formMountedAt.current,
        prompt_edit_count: promptEditCount,
        had_billing_pageview_before_gen:
          sessionStorage.getItem("visited_billing") === "1",
        is_autogen: autogen,
        image_source: imageSource,
      };
      if (meta) {
        params.image_width = meta.width;
        params.image_height = meta.height;
        params.image_megapixels = meta.megapixels;
      }

      if (effectiveModelId === INTEGRATED_MODEL_ID) {
        params.duration_seconds = integratedSeconds;
      }

      const result = await generateMutation.mutateAsync(params as any);
      clearDraft();
      if (result.video?.id) navigate(`/generation/${result.video.id}`);
    } catch (err) {
      console.error("Generation failed:", err);
    }
  }, [
    imageUrl,
    prompt,
    effectiveModelId,
    integratedSeconds,
    credits,
    displayCost,
    generateMutation,
    navigate,
    imageMeta,
    imageSource,
    promptEditCount,
    clerkUser?.id,
    hasPurchased,
  ]);

  const autoGenFiredRef = useRef(false);
  useEffect(() => {
    if (autoGenFiredRef.current) return;
    if (!isSignedIn || isUserLoading) return;
    if (!hasPendingAutogen()) return;
    if (!imageUrl || !prompt.trim()) return;
    autoGenFiredRef.current = true;
    clearPendingAutogen();
    handleGenerate({ autogen: true });
  }, [isSignedIn, isUserLoading, imageUrl, prompt, handleGenerate]);

  return (
    <div className="space-y-5">
      <UploadZone imageUrl={imageUrl} onImageChange={handleImageChange} />

      <div>
        <label className="block text-xs font-semibold text-[#8a8570] uppercase tracking-wider mb-2">
          Prompt
        </label>
        <textarea
          value={prompt}
          onChange={(e) => {
            setPrompt(e.target.value);
            setPromptEditCount((c) => c + 1);
          }}
          placeholder="A cinematic shot of a person walking through a neon-lit city at night, camera slowly panning..."
          rows={3}
          className="input-field resize-none"
        />
      </div>

      <ModelSelector
        value={modelId}
        onChange={setModelId}
        integratedSeconds={integratedSeconds}
        onIntegratedSecondsChange={setIntegratedSeconds}
        lockToAffiliateDefault={freeCreditsLocked}
        onRequireAuth={
          isSignedIn
            ? undefined
            : () => openAuthModal("Sign Up to Unlock")
        }
      />

      {isSignedIn && (
        <>
          <div className="flex items-center justify-between text-xs text-[#8a8570]">
            <span className="flex items-center gap-1.5">
              <Star className="size-3 text-amber-600" />
              {displayCost} stars · {selectedModel.label}
            </span>
            <span className="tabular-nums font-medium">
              Balance: <span className={credits >= displayCost ? "text-[#2d3127]" : "text-red-500"}>{credits.toLocaleString()}</span>
            </span>
          </div>
        </>
      )}

      <Show when="signed-out">
        <button
          onClick={() => {
            setPendingAutogen();
            setAuthTitle("Sign Up to View Generation");
            setFakeLoading(true);
            setTimeout(() => {
              setFakeLoading(false);
              setShowAuth(true);
            }, 2000);
          }}
          disabled={fakeLoading}
          className="btn-primary w-full py-3.5 text-[15px] flex items-center justify-center gap-2"
        >
          {fakeLoading ? (
            <><Loader2 className="size-4 animate-spin" />Generating...</>
          ) : (
            <><Sparkles className="size-4" />Generate Video</>
          )}
        </button>
        <AuthModal
          isOpen={showAuth}
          onClose={() => setShowAuth(false)}
          title={authTitle}
          previewImageUrl={authTitle === "Sign Up to Unlock" ? null : imageUrl}
        />
      </Show>

      <Show when="signed-in">
        <button
          onClick={() => handleGenerate()}
          disabled={!canGenerate || generateMutation.isPending}
          className="btn-primary w-full py-3.5 text-[15px] flex items-center justify-center gap-2"
        >
          {generateMutation.isPending ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="size-4" />
              Generate Video
            </>
          )}
        </button>
      </Show>

      {generateMutation.isError && (
        <NsfwOrError error={generateMutation.error} />
      )}

      <CreditsModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
        creditCost={displayCost}
        credits={credits}
      />
    </div>
  );
}
